import { useRef, useState, useEffect, useCallback } from "react";
import Input from "../../../../Components/Common/Input/Input";
import { Photo } from "../types/inspector.types";
import { uploadFileToS3 } from "../../../GcPrecon/GcPreconCommercial/UploadFileToS3";
import Button from "Components/Common/Button/Button";

interface PhotosTabProps {
  photos: Photo[];
  onAddPhoto: (newPhotos: Photo[]) => void;
  onUpdateCaption: (id: string, caption: string) => void;
  onPhotoUploaded: (id: string, s3Key: string) => void;
  onPhotoUploadFailed: (id: string, error: string) => void;
  onRemovePhoto: (id: string) => void;
}

export default function PhotosTab({
  photos,
  onAddPhoto,
  onUpdateCaption,
  onPhotoUploaded,
  onPhotoUploadFailed,
  onRemovePhoto,
}: PhotosTabProps) {
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const libraryInputRef = useRef<HTMLInputElement>(null);
  const uploadingFingerprints = useRef(new Set<string>());
  const [showCamera, setShowCamera] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const processFiles = useCallback(
    async (fileArray: File[], uploadType: "capture" | "upload") => {
      const filtered = fileArray.filter((file) => {
        const fp = `${file.name}-${file.size}`;
        if (uploadingFingerprints.current.has(fp)) return false;
        uploadingFingerprints.current.add(fp);
        return true;
      });
      if (filtered.length === 0) return;

      const newPhotos: Photo[] = filtered.map((file, idx) => ({
        id: `${Date.now()}-${idx}`,
        previewUrl: URL.createObjectURL(file),
        caption: "",
        uploadType,
        fileName: file.name,
        uploading: true,
      }));

      onAddPhoto(newPhotos);

      await Promise.allSettled(
        filtered.map(async (file, idx) => {
          const photoId = newPhotos[idx].id;
          const fp = `${file.name}-${file.size}`;
          try {
            const result = await uploadFileToS3(file);
            onPhotoUploaded(photoId, result.key);
          } catch (err) {
            const msg = err instanceof Error ? err.message : "Upload failed";
            onPhotoUploadFailed(photoId, msg);
          } finally {
            uploadingFingerprints.current.delete(fp);
          }
        }),
      );
    },
    [onAddPhoto, onPhotoUploaded, onPhotoUploadFailed],
  );

  const handleFiles = async (
    files: FileList | null,
    inputEl: HTMLInputElement,
    uploadType: "capture" | "upload",
  ) => {
    if (!files || files.length === 0) return;
    await processFiles(Array.from(files), uploadType);
    inputEl.value = "";
  };

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  const handleCameraClick = async () => {
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (isMobile) {
      const input = cameraInputRef.current;
      if (!input) return;
      input.setAttribute("capture", "environment");
      input.click();
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "environment",
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      });
      streamRef.current = stream;
      setShowCamera(true);
    } catch {
      const input = cameraInputRef.current;
      if (!input) return;
      input.removeAttribute("capture");
      input.click();
    }
  };

  useEffect(() => {
    if (showCamera && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play();
    }
  }, [showCamera]);

  useEffect(() => {
    return () => stopStream();
  }, []);

  const handleCapture = () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d")?.drawImage(video, 0, 0);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const file = new File([blob], `photo-${Date.now()}.jpg`, {
          type: "image/jpeg",
        });
        stopStream();
        setShowCamera(false);
        setVideoReady(false);
        processFiles([file], "capture");
      },
      "image/jpeg",
      0.92,
    );
  };

  const handleCameraClose = () => {
    stopStream();
    setShowCamera(false);
    setVideoReady(false);
  };

  return (
    <>
      <Input
        ref={cameraInputRef}
        className="hiddenInput"
        type="file"
        accept="image/*"
        tabIndex={-1}
        onChange={(e) => handleFiles(e.target.files, e.target, "capture")}
      />
      <Input
        ref={libraryInputRef}
        className="hiddenInput"
        type="file"
        accept="image/*"
        multiple
        tabIndex={-1}
        onChange={(e) => handleFiles(e.target.files, e.target, "upload")}
      />

      {showCamera && (
        <div className="cameraModal">
          <video
            ref={videoRef}
            className="cameraVideo"
            autoPlay
            playsInline
            muted
            onLoadedMetadata={() => setVideoReady(true)}
          />
          <div className="cameraControls">
            <Button
              type="button"
              className="cameraCloseBtn"
              onClick={handleCameraClose}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="cameraCaptureBtn"
              onClick={handleCapture}
              disabled={!videoReady}
            >
              {videoReady ? "📷 Capture" : "Starting…"}
            </Button>
          </div>
        </div>
      )}

      <div className="photoActions">
        <Button
          type="button"
          className="photoActionBtn"
          onClick={handleCameraClick}
          aria-label="Take photo"
        >
          <div className="paIcon">📷</div>
          <div className="paLabel">Take Photo</div>
          <div className="paSub">Use camera</div>
        </Button>
        <Button
          type="button"
          className="photoActionBtn"
          onClick={() => libraryInputRef.current?.click()}
          aria-label="Choose from library"
        >
          <div className="paIcon">🖼</div>
          <div className="paLabel">From Library</div>
          <div className="paSub">Choose existing</div>
        </Button>
      </div>

      <div className="photosGrid">
        {photos.map((photo) => (
          <div
            className={`photoThumb${photo.uploadError ? " photoThumbError" : ""}`}
            key={photo.id}
          >
            <div className="photoImg">
              {photo.uploading ? (
                <div className="photoUploadingOverlay">
                  <span className="photoSpinner" />
                </div>
              ) : photo.uploadError ? (
                <div className="photoErrorOverlay">
                  <span className="photoErrorIcon">!</span>
                  <span className="photoErrorMsg">{photo.uploadError}</span>
                  <Button
                    type="button"
                    className="photoRemoveBtn"
                    onClick={() => onRemovePhoto(photo.id)}
                    aria-label="Remove failed photo"
                  >
                    Remove
                  </Button>
                </div>
              ) : photo.previewUrl ? (
                <img
                  src={photo.previewUrl}
                  alt={photo.caption || "Inspection photo"}
                />
              ) : (
                <div className="photoFilename">
                  <span className="photoFilenameIcon">📄</span>
                  <span className="photoFilenameText">{photo.fileName}</span>
                </div>
              )}
            </div>
            {!photo.uploadError && (
              <div className="photoCaptionWrap">
                <Input
                  className="photoCaptionInput"
                  placeholder="Add caption…"
                  value={photo.caption}
                  onChange={(e) => onUpdateCaption(photo.id, e.target.value)}
                  aria-label="Photo caption"
                  disabled={photo.uploading}
                />
                <Button
                  type="button"
                  className="photoRemoveBtn"
                  onClick={() => onRemovePhoto(photo.id)}
                  disabled={photo.uploading}
                  aria-label="Remove photo"
                >
                  Remove
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}
